import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { ServiceBooking, ServiceFeatures } from '@mirai/services';
import { Action, Notification, Progress, styles, useDevice, View } from '@mirai/ui';
import React, { useEffect, useState } from 'react';

import { ACTION, CES_SURVEY_FEATURE, ERROR, STATE } from './Booking.constants';
import { L10N } from './Booking.l10n';
import * as style from './Booking.module.css';
import Skeleton from './Booking.skeleton';
import { Hotelverse } from './components';
import {
  Information,
  Menu,
  Modal,
  ModalBoard,
  ModalCalendar,
  ModalCesSurvey,
  ModalOccupation,
  ModalRoomSelection,
} from './partials';
import { BookingPayment } from './partials/Booking.Payment';
import { useEditFlow } from './partials/hooks';
import { getSafeTrust } from '../../trust';
import { BookingDetails, BookingSummary, BookingTerms, Footer } from '../__shared__';
import { Header } from '../Header';
import { EVENT } from '../helpers';

const Booking = ({ ...others }) => {
  const { isDesktop, isMobile } = useDevice();
  const { locale, translate } = useLocale();
  const { value: store = {} } = useStore();
  const {
    hotel: { url } = {},
    id: instanceId,
    skeleton = false,
    urlParams: { code: token, reservationCode: id, hasToTracking: tracking } = {},
  } = store;
  const trust = getSafeTrust(store);

  const [action, setAction] = useState();
  const [calendar, setCalendar] = useState();
  const [cesSurveyVisible, setCesSurveyVisible] = useState(false);
  const [dataSource, setDataSource] = useState();
  const [expanded, setExpanded] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [responseCalendar, setResponseCalendar] = useState();
  const [responseError, setResponseError] = useState();
  const cesSurveyEnabled = !!(dataSource?.showCesSurvey && ServiceFeatures.get(CES_SURVEY_FEATURE, instanceId));

  useEffect(() => {
    if (!skeleton && dataSource) trust.booking(dataSource, store);
  }, [dataSource, skeleton, store, trust]);

  const boardFlow = useEditFlow({
    dataSource,
    modifyKey: 'board',
    confirmAction: ACTION.EDIT_BOARD_CONFIRM,
    setAction,
    setMenuVisible,
  });

  const occupationFlow = useEditFlow({
    dataSource,
    modifyKey: 'occupation',
    confirmAction: ACTION.EDIT_OCCUPATION_CONFIRM,
    setAction,
    setMenuVisible,
  });

  useEffect(() => {
    if (skeleton || !id || !token) return;

    (async () => {
      setDataSource(
        await ServiceBooking.get({ id, locale, token, tracking }).catch((error) => setResponseError(error)),
      );
      setAction();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, locale]);

  useEffect(() => {
    if (!dataSource || !dataSource.tracking) return;

    const event = dataSource.state === STATE.CANCELLED ? EVENT.BOOKING_CANCEL : EVENT.BOOKING_RESPONSE;
    Event.publish(event, { event, response: { ...dataSource } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource?.state]);

  useEffect(() => {
    if (!cesSurveyEnabled) {
      setCesSurveyVisible(false);
      return;
    }

    const timeout = setTimeout(() => setCesSurveyVisible(true), 4000);

    return () => clearTimeout(timeout);
  }, [cesSurveyEnabled]);

  const handleChange = ({ response: { tracking, ...response } = {}, state }) => {
    const isConfirmed = response?.state === STATE.CONFIRMED;
    if (state || isConfirmed) {
      setDataSource({ ...(isConfirmed ? response : dataSource), tracking, ...(isConfirmed ? undefined : { state }) });
    }
  };

  const handleConfirmCalendar = (response) => {
    setResponseCalendar(response);
    handleMenuPress(ACTION.EDIT_DATE);
  };

  const handleMenuPress = (action) => {
    if (action === ACTION.PRINT) {
      setExpanded(true);
      setTimeout(window.print, 10);
    } else if (action === ACTION.SHOW_CALENDAR) {
      setCalendar(true);
    } else if (action === ACTION.EDIT_BOARD) {
      boardFlow.open();
    } else if (action === ACTION.EDIT_OCCUPATION) {
      occupationFlow.open();
    } else {
      setAction(action);
    }
  };

  const handleBackHome = () => {
    const { payload } = responseError;

    window.location.href = payload?.hotelUrl || url;
  };

  const { payment, state } = dataSource || {};
  const response = dataSource !== undefined;
  const isCancelled = state === STATE.CANCELLED;

  return (
    <>
      <Header />

      <View role="booking" testId={others.testId} className={styles(style.container, others.className)}>
        <Progress indeterminate visible={!response && !responseError} className={style.progress} />
        {response ? (
          <>
            <View row={isDesktop} className={style.content}>
              <View>
                {state !== STATE.POS_ERROR && (
                  <Notification
                    error={isCancelled}
                    info={state === STATE.COMPLETED}
                    success={state === STATE.CONFIRMED}
                    warning={state === STATE.POSTPONED}
                    wide
                  >
                    {translate(L10N.NOTIFICATION[state])}
                    <Hotelverse {...dataSource} />
                  </Notification>
                )}

                {payment && !isCancelled && <BookingPayment {...{ dataSource, onError: setResponseError }} />}

                <BookingSummary
                  box={!isDesktop}
                  dataSource={{ ...dataSource, showBasePrice: true }}
                  className={styles(isDesktop && style.print)}
                />

                <Information dataSource={dataSource} />
                <BookingDetails dataSource={dataSource} expanded={expanded || isMobile} className={style.box} />
                <BookingTerms dataSource={dataSource} small trackBookingInteraction />
              </View>

              <View className={style.side}>
                {isDesktop && (
                  <BookingSummary box dataSource={{ ...dataSource, showBasePrice: true }} className={style.noPrint} />
                )}

                <Menu
                  dataSource={dataSource}
                  visible={menuVisible}
                  onPress={handleMenuPress}
                  onVisibleChange={setMenuVisible}
                />
              </View>
            </View>

            <Modal
              {...{
                action,
                dataSource: { ...dataSource, token },
                responseBoard: boardFlow.response,
                responseCalendar,
                responseOccupation: occupationFlow.response,
              }}
              onChange={handleChange}
              onClose={(nextAction) => {
                if (nextAction === ACTION.EDIT_BOARD && action === ACTION.EDIT_BOARD_CONFIRM) {
                  setAction();
                  boardFlow.open({ fromConfirm: true, room: boardFlow.response?.room });
                  return;
                }

                if (nextAction === ACTION.EDIT_OCCUPATION && action === ACTION.EDIT_OCCUPATION_CONFIRM) {
                  setAction();
                  occupationFlow.open({ fromConfirm: true, room: occupationFlow.response?.room });
                  return;
                }

                setAction();
                if (Object.values(ACTION).includes(nextAction)) handleMenuPress(nextAction);
              }}
            />
            <ModalCalendar
              dataSource={dataSource}
              token={token}
              visible={calendar}
              onConfirm={handleConfirmCalendar}
              onClose={() => {
                setCalendar();
                setAction();
              }}
            />
            <ModalCesSurvey
              id={dataSource.id}
              token={token}
              visible={cesSurveyVisible}
              onClose={() => setCesSurveyVisible(false)}
            />
            <ModalBoard
              dataSource={dataSource}
              room={boardFlow.room}
              token={token}
              visible={boardFlow.visible}
              onBack={boardFlow.back}
              onConfirm={boardFlow.confirm}
              onClose={boardFlow.close}
            />
            <ModalOccupation
              dataSource={dataSource}
              room={occupationFlow.room}
              token={token}
              visible={occupationFlow.visible}
              onBack={occupationFlow.back}
              onConfirm={occupationFlow.confirm}
              onClose={occupationFlow.close}
            />
            <ModalRoomSelection
              dataSource={dataSource}
              rooms={dataSource?.modify?.board?.rooms}
              title={translate(L10N.ACTION_EDIT_BOARD)}
              visible={boardFlow.roomSelection}
              onBack={boardFlow.backRoomSelection}
              onNext={boardFlow.selectRoom}
              onClose={boardFlow.closeRoomSelection}
              testId="booking-edit-board"
            />
            <ModalRoomSelection
              dataSource={dataSource}
              title={translate(L10N.ACTION_EDIT_OCCUPATION)}
              visible={occupationFlow.roomSelection}
              onBack={occupationFlow.backRoomSelection}
              onNext={occupationFlow.selectRoom}
              onClose={occupationFlow.closeRoomSelection}
              testId="booking-edit-occupation"
            />
          </>
        ) : responseError ? (
          <Notification error large className={style.error}>
            {translate(L10N.NOTIFICATION_ERROR[responseError.code] || L10N.NOTIFICATION_ERROR[ERROR.GENERIC])}
            <Action paragraph underline wide onPress={handleBackHome}>
              {translate(L10N.ACTION_BACK_HOME)}
            </Action>
          </Notification>
        ) : (
          <Skeleton />
        )}
      </View>

      <Footer />
    </>
  );
};

Booking.displayName = 'Mirai:Core:Booking';

Booking.propTypes = {};

export { Booking };
